/**
 * Events and sampling templates — 3 fill-in-the-blank documents.
 *
 * Three different jobs, and a founder should be able to tell them apart at a glance:
 *   event-participation-agreement — the deal with the organiser for space at an event
 *   demo-contractor-agreement     — the deal with the person standing behind the table
 *   product-sampling-release      — the one-page form the person tasting the product signs
 *
 * Two drafting rules run through all three. Permits, licences and handling certifications are
 * written as a party's responsibility to determine and obtain what applies to its own activity —
 * never as a statement of what any rule requires, because that varies by venue, location and
 * activity and neither this file nor the founder can know it. And food-safety practices in the
 * demo agreement are contractual obligations the Demonstrator owes the Company, phrased as
 * promises rather than as a description of anybody's rules.
 *
 * Every body uses visible [BRACKET] blanks. An unfilled blank stays on the page on purpose.
 * The renderer prepends the standard disclaimer; nothing here competes with it.
 */

export const EVENTS_TEMPLATES = [
  {
    id: "event-participation-agreement",
    docId: "event-participation-agreement",
    title: "EVENT PARTICIPATION AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "You are booking space at an event to exhibit, sample or sell — a trade show, a food festival, a farmers market, a music event, a conference, a pop-up. Use it when the organiser has no paper of its own, or as the checklist you read the organiser's own terms against before you click accept.",
      whyItMatters:
        "The booth fee is almost never the largest number. Power, refrigeration, ice, drayage, storage and waste disposal are usually charged separately and can together exceed what you paid for the space. The second cost is product: a multi-day activation for a beverage brand can consume more inventory than a small retail order, and sampled product does not go back into saleable stock. The third is the one that ends the weekend early — arriving without the permit, the certificate of insurance or the handling arrangement the venue expects, and being told at load-in that you cannot serve.",
      negotiateCarefully: [
        {
          point: "A fully loaded quote before you commit",
          detail:
            "Ask for every chargeable item in writing — power drops, water, refrigeration, drayage, forklift, storage, wifi, cleaning, waste and grey water — and get the rates before you sign, not after. This is where the real cost of the event lives, and organisers will quote it if asked.",
        },
        {
          point: "Whether sampling and selling are actually permitted",
          detail:
            "An exhibitor agreement that permits display but not sampling or on-site sale removes the entire reason you booked. Get both answers in writing, and get them separately, because they are frequently different.",
        },
        {
          point: "Who is responsible for permits and handling requirements",
          detail:
            "Requirements for serving and selling food and drink vary by venue, location and activity. The organiser saying yes is not the same as the local requirement being met. Make the agreement say who determines what applies and who obtains it, and start that work weeks before load-in rather than days.",
        },
        {
          point: "What happens if the event is cancelled, postponed or moved",
          detail:
            "Your product, freight, staff and travel are committed long before the organiser decides. Ask for a stated refund or credit position for cancellation, for postponement, and for a material change of venue or dates — and check whether the force majeure clause lets the organiser keep the whole fee.",
        },
        {
          point: "What category exclusivity actually buys",
          detail:
            "Exclusivity is often narrower than the word suggests. Pin down the category definition, whether it covers exhibitors only or also sponsors, food trucks, bars, retail outlets in the venue and event partners, and what happens if a competitor turns up anyway — because a refund is usually the only remedy on offer.",
        },
      ],
      redFlags: [
        "A fee quoted with no schedule of additional charges attached.",
        "No refund or credit of any kind if the organiser cancels, postpones or relocates.",
        "Insurance limits and endorsements you have not checked against your actual policy.",
        "Silence on whether sampling and on-site sales are permitted.",
        "An indemnity that runs only in the organiser's favour.",
        "An exclusivity fee with no written definition of the category it covers.",
      ],
      timeToComplete: "30 minutes to fill · sign well before load-in, not on the day",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "March 3, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Your product category", brandKey: "productCategory", example: "Functional beverage" },
      { token: "[ORGANISER NAME]", label: "Organiser's legal name", example: "Riverfront Festivals, Inc." },
      { token: "[ORGANISER STATE]", label: "Organiser's state of formation", example: "Texas" },
      { token: "[ORGANISER ENTITY TYPE]", label: "Organiser's entity type", example: "corporation" },
      { token: "[ORGANISER ADDRESS]", label: "Organiser's address", example: "9 Riverside Dr, Austin, TX 78704" },
      { token: "[EVENT NAME]", label: "Name of the event", example: "Riverfront Food and Drink Festival 2026" },
      { token: "[EVENT VENUE]", label: "Venue and address", example: "Auditorium Shores, Austin, TX" },
      { token: "[EVENT DATES]", label: "Event dates", example: "May 15–17, 2026" },
      { token: "[EVENT HOURS]", label: "Hours the space must be staffed", example: "11:00 to 20:00 each day" },
      { token: "[SPACE DESCRIPTION]", label: "The space: number, size, location", example: "Booth C-14, 10 ft x 10 ft, end cap on the main aisle" },
      { token: "[ACTIVATION DESCRIPTION]", label: "What you are doing in the space", example: "branded bar with chilled sampling, product display and a photo wall" },
      { token: "[LOAD IN WINDOW]", label: "Load-in window", example: "May 14, 08:00 to 18:00" },
      { token: "[LOAD OUT WINDOW]", label: "Load-out window", example: "May 17, 20:00 to 23:59" },
      { token: "[PARTICIPATION FEE]", label: "Total participation fee", example: "$4,500" },
      { token: "[DEPOSIT AMOUNT]", label: "Deposit", example: "$1,500" },
      { token: "[DEPOSIT DUE DATE]", label: "Deposit due date", example: "March 10, 2026" },
      { token: "[BALANCE DUE DATE]", label: "Balance due date", example: "April 15, 2026" },
      { token: "[PAYMENT TERMS]", label: "Payment terms and method", example: "ACH to the account on the invoice, net 15" },
      { token: "[INCLUDED SERVICES]", label: "Exactly what the fee includes", example: "the space, one 6 ft table, two chairs, one exhibitor sign, four staff credentials and general aisle cleaning" },
      { token: "[ADDITIONAL CHARGES]", label: "Chargeable extras and their rates", example: "power drops, water and drain, refrigeration rental, ice, drayage, forklift, on-site storage, wifi, in-booth cleaning and grey water removal" },
      { token: "[ORGANISER INFORMATION DATE]", label: "Date the organiser must send floor plan, rules and venue requirements", example: "April 1, 2026" },
      { token: "[SAMPLING PERMITTED]", label: "Is sampling permitted — yes or no, with any limits", example: "yes, chilled samples only, maximum 4 fl oz per serving" },
      { token: "[ON SITE SALES PERMITTED]", label: "Is on-site sale permitted — yes or no, with any limits", example: "yes, single cans and 4-packs only, cashless" },
      { token: "[SAMPLE FORMAT]", label: "How samples will be served", example: "poured on site from sealed cases into single-use 2 fl oz cups" },
      { token: "[LIABILITY LIMIT PER OCCURRENCE]", label: "Required liability limit per occurrence", example: "$1,000,000" },
      { token: "[LIABILITY LIMIT AGGREGATE]", label: "Required liability limit in the aggregate", example: "$2,000,000" },
      { token: "[ADDITIONAL COVERAGE]", label: "Any other coverage required", example: "product liability and workers compensation for staff working the space" },
      { token: "[ADDITIONAL INSUREDS]", label: "Who must be named as additional insured", example: "the Organiser and the venue owner, as their interests appear" },
      { token: "[CERTIFICATE DUE DATE]", label: "Date the certificate of insurance is due", example: "April 15, 2026" },
      { token: "[EXCLUSIVITY PURCHASED]", label: "Has category exclusivity been purchased — yes or no", example: "yes" },
      { token: "[EXCLUSIVITY CATEGORY]", label: "The category, defined precisely", example: "non-alcoholic canned energy and functional beverages" },
      { token: "[EXCLUSIVITY SCOPE]", label: "What the exclusivity covers", example: "exhibitors and sponsors across the whole event site, excluding permanent venue outlets and licensed bars" },
      { token: "[EXCLUSIVITY FEE]", label: "Fee for exclusivity", example: "$3,000, included in the participation fee" },
      { token: "[EXCLUSIVITY REMEDY]", label: "Remedy if exclusivity is not honoured", example: "refund of the exclusivity fee plus a credit against the following year's participation fee" },
      { token: "[MARK USE SCOPE]", label: "How the organiser may use your marks", example: "the event website, printed programme, on-site signage and the organiser's organic social channels" },
      { token: "[EVENT CONTENT USE]", label: "How you may use content shot at the event", example: "organic social, the brand website and trade sell sheets, with an event credit" },
      { token: "[WASTE ARRANGEMENT]", label: "Waste, recycling and grey water arrangement", example: "the Exhibitor bags and removes its own waste to the compound; grey water is collected by the Organiser twice daily" },
      { token: "[EARLY CANCELLATION DATE]", label: "First cancellation cut-off", example: "March 31, 2026" },
      { token: "[EARLY CANCELLATION REFUND]", label: "Refund if cancelled before that date", example: "the full fee less the deposit" },
      { token: "[LATE CANCELLATION DATE]", label: "Second cancellation cut-off", example: "April 30, 2026" },
      { token: "[LATE CANCELLATION REFUND]", label: "Refund if cancelled before the second date", example: "half of the fee paid" },
      { token: "[ORGANISER CANCELLATION REFUND]", label: "Refund if the organiser cancels", example: "the full fee paid, refunded within 30 days" },
      { token: "[POSTPONEMENT POSITION]", label: "What happens if the event is postponed or relocated", example: "a credit against the rescheduled date, or a full refund if the Exhibitor cannot attend the new date" },
      { token: "[FORCE MAJEURE REFUND BASIS]", label: "Refund basis where a force majeure event stops the event", example: "a pro-rata refund of the fee for the days not held, less documented costs already incurred by the Organiser" },
      { token: "[CURE PERIOD]", label: "Cure period for a breach", example: "10 days" },
      { token: "[LIABILITY CAP]", label: "Cap on each party's liability", example: "the participation fee paid" },
      { token: "[NOTICE METHOD]", label: "How notices must be sent", example: "email with confirmed receipt, plus courier to the addresses above" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Texas" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Travis County, Texas" },
    ],

    body: `This Event Participation Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE]
(the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business at
  [COMPANY ADDRESS], which markets [PRODUCT CATEGORY] products under the brand [BRAND NAME]
  (the "Exhibitor"); and

  [ORGANISER NAME], a [ORGANISER STATE] [ORGANISER ENTITY TYPE], with a principal place of
  business at [ORGANISER ADDRESS] (the "Organiser").

1. THE EVENT
The Organiser is producing [EVENT NAME] (the "Event") at [EVENT VENUE] on [EVENT DATES]. The
Exhibitor shall staff its space throughout [EVENT HOURS].

2. THE SPACE AND THE ACTIVATION
The Organiser grants the Exhibitor the right to occupy [SPACE DESCRIPTION] (the "Space") for the
duration of the Event. The Exhibitor will use the Space for [ACTIVATION DESCRIPTION] (the
"Activation").

The Exhibitor shall not sublet, share, assign or transfer the Space, and shall not display,
sample, sell or promote any brand other than [BRAND NAME] from the Space, without the Organiser's
prior written consent. The Organiser shall not relocate the Space after the floor plan is issued
under Section 5 without the Exhibitor's prior written consent, except where the venue or a public
authority requires the change, in which case the Organiser shall offer the Exhibitor a comparable
position or a refund of the participation fee.

3. LOAD-IN AND LOAD-OUT
Load-in: [LOAD IN WINDOW]. Load-out: [LOAD OUT WINDOW].

The Exhibitor shall not begin dismantling the Activation before the end of the Event. Anything
left in the Space after the load-out window may be removed and stored by the Organiser at the
Exhibitor's cost.

4. FEES AND WHAT THEY COVER
Participation fee: [PARTICIPATION FEE]. A deposit of [DEPOSIT AMOUNT] is due on
[DEPOSIT DUE DATE] and the balance is due on [BALANCE DUE DATE]. Payment terms: [PAYMENT TERMS].

The participation fee covers the following and nothing else: [INCLUDED SERVICES].

The following are not covered by the participation fee and are chargeable in addition:
[ADDITIONAL CHARGES]. The Organiser shall provide the Exhibitor with a written rate schedule or
quotation for any such item before the Exhibitor orders it, and shall not charge the Exhibitor for
any service the Exhibitor did not order in writing, except for services the venue imposes on all
exhibitors and which the Organiser has notified in advance.

5. WHAT THE ORGANISER PROVIDES
The Organiser shall:
(a) make the Space and the included services available throughout the Event and the load-in and
    load-out windows;
(b) deliver to the Exhibitor, no later than [ORGANISER INFORMATION DATE], the floor plan showing
    the Space, the Event rules, the venue's requirements applicable to exhibitors, the schedule of
    additional charges, and the contact details of the on-site manager;
(c) tell the Exhibitor in writing what the Organiser itself requires in connection with sampling,
    on-site sales, food handling, waste and grey water at the Event; and
(d) operate the Event substantially as described in the materials on which the Exhibitor booked.

6. SAMPLING AND ON-SITE SALES
Sampling of the Exhibitor's product from the Space is permitted: [SAMPLING PERMITTED].
On-site sale of the Exhibitor's product from the Space is permitted: [ON SITE SALES PERMITTED].

Where sampling is permitted, the Exhibitor shall serve samples in the format described as
[SAMPLE FORMAT], shall make ingredient and allergen information for each product being sampled
visible and legible at the Space at all times while sampling, shall keep product under the
temperature and handling conditions the Exhibitor has determined are appropriate for it, and shall
stop sampling any product it cannot serve under those conditions.

7. PERMITS, LICENCES AND APPROVALS
Requirements for serving, selling, storing and handling food and beverage at an event vary by
venue, location and activity, and neither party should assume the other has dealt with them.

The Exhibitor is responsible for determining which permits, licences, registrations,
certifications and approvals apply to the Activation, and for obtaining them and paying for them
before load-in. The Exhibitor shall keep copies available at the Space during the Event and shall
provide copies to the Organiser on request.

The Organiser is responsible for determining and obtaining what applies to the Event as a whole
and to the Organiser's own activities.

Acceptance of this Agreement, of the Exhibitor's booking or of the Exhibitor's fee by the
Organiser is not a determination that the Activation meets any requirement, and is not a
substitute for anything the Exhibitor may need to obtain. Any approval the Organiser gives is
limited to the Organiser's own Event rules.

8. INSURANCE
The Exhibitor shall maintain, for the duration of the Event and the load-in and load-out windows,
commercial general liability insurance with limits of not less than
[LIABILITY LIMIT PER OCCURRENCE] per occurrence and [LIABILITY LIMIT AGGREGATE] in the aggregate,
together with the following further coverage: [ADDITIONAL COVERAGE].

The Exhibitor shall name the following as additional insureds: [ADDITIONAL INSUREDS], and shall
deliver a certificate of insurance evidencing that coverage, and any endorsement required by this
Section, to the Organiser by [CERTIFICATE DUE DATE]. If the certificate is not delivered by that
date, the Organiser may withhold access to the Space until it is.

The Organiser shall maintain insurance appropriate to the operation of the Event and shall provide
evidence of it to the Exhibitor on request.

9. INDEMNITY
Each party shall indemnify, defend and hold harmless the other party and its officers, directors,
employees and agents from and against third-party claims, and reasonable costs and expenses
associated with them, to the extent arising from the indemnifying party's negligence, wilful
misconduct or breach of this Agreement.

Without limiting the foregoing, the Exhibitor's indemnity extends to claims arising from its
product and the conduct of the Activation, and the Organiser's indemnity extends to claims arising
from the operation of the Event and the condition of the areas the Organiser controls. Neither
party is required to indemnify the other for the other's own negligence or wilful misconduct. The
party seeking indemnity shall give prompt written notice of the claim and shall cooperate in its
defence.

10. CATEGORY EXCLUSIVITY
Category exclusivity purchased: [EXCLUSIVITY PURCHASED]. Fee for exclusivity: [EXCLUSIVITY FEE].

Where exclusivity is purchased, the exclusive category is defined as [EXCLUSIVITY CATEGORY], and
the exclusivity covers [EXCLUSIVITY SCOPE].

The Organiser shall not grant space, a sponsorship or a promotional right within the exclusive
category, within the scope stated, to any party other than the Exhibitor. The exclusivity does not
restrict: attendees; permanent outlets, bars, restaurants and retailers operated by the venue and
not booked through the Organiser; products outside the defined category; or the sale or service of
any product the Organiser is contractually required to permit under an agreement in place before
the Effective Date and disclosed to the Exhibitor in writing.

If the Organiser does not honour the exclusivity, the Exhibitor's remedy is
[EXCLUSIVITY REMEDY].

Where exclusivity is not purchased, the Organiser gives no assurance about which other brands will
be present, and placement on the floor plan is at the Organiser's discretion.

11. USE OF THE EXHIBITOR'S MARKS
The Exhibitor grants the Organiser a non-exclusive, non-transferable, royalty-free licence to use
the Exhibitor's name, logo and product images solely to promote and report on the Event, in the
following media and nowhere else: [MARK USE SCOPE].

The Organiser shall use the marks in the form the Exhibitor supplies, shall follow any brand
guidelines the Exhibitor provides, shall not alter or combine them without consent, and shall not
present the Exhibitor as a sponsor of the Event or as endorsing the Organiser, any sponsor or any
other exhibitor. This licence ends when the Event ends, except that the Organiser may leave
existing recaps and archive pages in place. The Organiser shall remove the Exhibitor's marks from
any future Event marketing on the Exhibitor's written request.

12. PHOTOGRAPHY AND CONTENT AT THE EVENT
The Organiser may photograph and film the Event, including general views that include the Space
and the Activation, and may use that footage to promote and report on the Event. The Organiser
shall not use footage that features the Exhibitor's product or marks as the principal subject in
paid advertising, or in any material promoting another brand, without the Exhibitor's prior
written consent.

The Exhibitor may photograph and film its own Space and Activation and may use that content as
follows: [EVENT CONTENT USE]. Each party is responsible for obtaining any release it needs from
individuals who appear in content it creates, and neither party grants the other any right in the
other's marks beyond what this Agreement states.

13. CONDUCT, SAFETY AND WASTE
The Exhibitor shall: keep the Activation within the boundaries of the Space; not obstruct aisles,
exits or neighbouring spaces; follow the Event rules and the venue's rules once provided; keep the
Space attended during Event hours; not distribute product or materials outside the Space without
consent; and manage waste, recycling and grey water as follows: [WASTE ARRANGEMENT].

14. CANCELLATION BY THE EXHIBITOR
If the Exhibitor cancels on or before [EARLY CANCELLATION DATE], the Organiser shall refund
[EARLY CANCELLATION REFUND]. If the Exhibitor cancels after that date but on or before
[LATE CANCELLATION DATE], the Organiser shall refund [LATE CANCELLATION REFUND]. If the Exhibitor
cancels after [LATE CANCELLATION DATE], no refund is due. Cancellation must be given in writing
under Section 18.

15. CANCELLATION, POSTPONEMENT AND FORCE MAJEURE
If the Organiser cancels the Event, the Organiser shall refund [ORGANISER CANCELLATION REFUND].

If the Organiser postpones the Event, changes its dates, or moves it to a different venue, the
Exhibitor's position is: [POSTPONEMENT POSITION].

"Force Majeure Event" means an event beyond the reasonable control of the affected party,
including severe weather, fire, flood, loss of power or water, damage to or closure of the venue,
public health measures, civil disturbance and the act or order of a public authority. If a Force
Majeure Event prevents the Event from being held in whole or in part, the affected party shall
give the other prompt written notice, and the refund basis is: [FORCE MAJEURE REFUND BASIS].

Neither party is responsible for the other party's travel, freight, product, staffing, build or
accommodation costs arising from a cancellation, postponement, relocation or Force Majeure Event,
except as this Section states.

16. TERM AND TERMINATION
This Agreement runs from the Effective Date until the Exhibitor has completed load-out and both
parties have met their payment obligations.

Either party may terminate this Agreement if the other materially breaches it and does not cure
the breach within [CURE PERIOD] after written notice describing it. The Organiser may also remove
the Exhibitor from the Event, without refund, if the Exhibitor's conduct at the Event creates a
safety risk, violates the venue's rules after a written warning, or involves selling or sampling
that the Exhibitor has been told in writing is not permitted.

Sections 9, 11, 12, 17, 19 and 20 survive termination or expiry.

17. LIMITATION OF LIABILITY
Neither party is liable to the other for indirect, incidental, special, consequential or punitive
damages, or for lost profits, in connection with this Agreement. Except for a party's
indemnification obligations under Section 9 and the Exhibitor's obligation to pay the
participation fee, each party's total liability under this Agreement is limited to
[LIABILITY CAP]. Nothing in this Section limits liability that cannot be limited by agreement.

18. NOTICES
Notices under this Agreement must be in writing and sent to the addresses stated above by the
following method: [NOTICE METHOD]. A notice is effective when received.

19. GOVERNING LAW AND VENUE
This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard to
its conflict of laws principles. The parties consent to venue in [VENUE].

20. GENERAL
This Agreement, together with the Event rules and any written rate schedule the Organiser provides
under Section 5, is the entire agreement between the parties regarding the Exhibitor's
participation in the Event and supersedes all prior discussions. Where the Event rules conflict
with this Agreement, this Agreement controls. This Agreement may be amended only in a writing
signed by both parties. The parties are independent contractors, and nothing here creates a
partnership, joint venture or agency between them. Neither party may assign this Agreement without
the other's prior written consent. If any provision is held unenforceable, the remainder continues
in effect. This Agreement may be signed in counterparts, including by electronic signature, each
of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [ORGANISER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________
`,
  },

  {
    id: "demo-contractor-agreement",
    docId: "demo-contractor-agreement",
    title: "DEMO CONTRACTOR AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "You are engaging an individual directly to run in-store demos or sampling shifts for your brand, rather than booking through a retailer's own demo programme or a staffing agency.",
      whyItMatters:
        "The person behind the table is the only human contact most shoppers will ever have with your brand, and everything they say is said on your behalf, inside somebody else's store, to somebody about to buy. Two things go wrong. The first is what gets said: an improvised benefit claim or a wrong answer to an allergen question is yours, not theirs. The second is structural — you post the schedule, name the store, supply the product and the table, script the pitch and require a shirt, and then paper it as a contractor engagement. How that relationship should be structured depends on how the work is actually controlled, and it is far cheaper to take advice before the first shift than to unwind it later.",
      negotiateCarefully: [
        {
          point: "How much you direct the work",
          detail:
            "Set the outcome and the standards in the agreement, and take counsel's view before you build a standing team you schedule, supervise and dress. What the document is called matters much less than how the work actually runs, and this is the one issue in this template worth paying for advice on.",
        },
        {
          point: "What they are allowed to say",
          detail:
            "Give them written talking points and a written list of claims they may not make, and make using both a contractual obligation. For an ingestible product this is the difference between a demo and a problem, and 'use your own words' is not a briefing.",
        },
        {
          point: "Handling, hygiene and who arranges certification",
          detail:
            "Write the handling practices into the agreement as obligations — temperature control, handwashing, gloves, discard rules, no consumption of returned product — and say plainly who determines what certification applies at each location, who obtains it, and who pays for it.",
        },
        {
          point: "Product accountability and lot records",
          detail:
            "Demo product is inventory leaving the building. Require a record of what was drawn, from which lot, what was served and what came back, so a withdrawal can reach it and so you know what a shift actually costs.",
        },
        {
          point: "Content and reporting as deliverables, not favours",
          detail:
            "Make the shift report and the photos contractual deliverables with a deadline, take an assignment of anything they shoot, and take a likeness release covering paid advertising. Store-level intelligence and demo footage are the two things you cannot buy afterwards.",
        },
      ],
      redFlags: [
        "A recurring, closely directed demo team on paperwork nobody has read against how the work actually runs.",
        "No written talking points or prohibited-claims list for an ingestible product.",
        "No allergen and ingredient information supplied for the table.",
        "Nobody has determined what handling certification applies at the stores you are booking.",
        "Demo product leaving with no lot record and no cost attribution.",
        "Demo photography used in paid ads with no assignment and no release from the demonstrator.",
      ],
      timeToComplete: "25 minutes to fill · sign before the first shift is booked",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "April 6, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Your product category", brandKey: "productCategory", example: "Functional beverage" },
      { token: "[DEMONSTRATOR NAME]", label: "Demonstrator's full legal name", example: "Jordan Ellis" },
      { token: "[DEMONSTRATOR ADDRESS]", label: "Demonstrator's address", example: "44 Cedar Ln, Austin, TX 78702" },
      { token: "[DEMONSTRATOR EMAIL]", label: "Demonstrator's email", example: "jordan.ellis@example.com" },
      { token: "[TERRITORY]", label: "Area the demonstrator covers", example: "Austin metro" },
      { token: "[LOCATIONS]", label: "Stores and locations covered", example: "the stores listed in the booking schedule the Company issues each month" },
      { token: "[SCHEDULE PROCESS]", label: "How shifts are offered and accepted", example: "the Company offers shifts by email at least 14 days ahead; the Demonstrator accepts or declines within 3 days" },
      { token: "[SHIFT LENGTH]", label: "Standard shift length", example: "4 hours, plus 30 minutes setup and 30 minutes breakdown" },
      { token: "[RATE]", label: "Rate", example: "$28 per hour, minimum 4 hours per shift" },
      { token: "[EXPENSE POLICY]", label: "Which expenses are reimbursed and which are not", example: "mileage beyond 25 miles from home, parking and ice; supplies and meals are not reimbursed" },
      { token: "[EXPENSE APPROVAL LIMIT]", label: "Amount above which expenses need pre-approval", example: "$50 per shift" },
      { token: "[INVOICE SCHEDULE]", label: "When invoices are submitted", example: "every two weeks, covering completed shifts" },
      { token: "[PAYMENT TERMS]", label: "Payment terms", example: "net 15 from a correct invoice, by ACH" },
      { token: "[SUPPLIES PROVIDED]", label: "What the company supplies", example: "product, cooler, thermometer, cups, gloves, sanitiser, table cover, signage and the allergen sheet" },
      { token: "[DEMONSTRATOR SUPPLIES]", label: "What the demonstrator supplies", example: "transport, phone, black trousers and closed-toe shoes" },
      { token: "[COLD HOLDING TEMPERATURE]", label: "Temperature chilled product must be held at or below", example: "40°F" },
      { token: "[TEMPERATURE CHECK INTERVAL]", label: "How often temperature is checked and logged", example: "every hour" },
      { token: "[SAMPLE DISCARD WINDOW]", label: "How long a poured or portioned sample may sit before it is discarded", example: "30 minutes" },
      { token: "[CERTIFICATION RESPONSIBILITY]", label: "Who determines, obtains and pays for any handling certification", example: "the Demonstrator determines and obtains it; the Company reimburses the fee on production of a receipt" },
      { token: "[TALKING POINTS DOCUMENT]", label: "Name of the approved talking points document", example: "the Acme Demo Script, current version" },
      { token: "[PROHIBITED CLAIMS DOCUMENT]", label: "Name of the prohibited claims list", example: "the Acme Claims We Do Not Make sheet, current version" },
      { token: "[REPORTING DEADLINE]", label: "When the shift report is due", example: "within 24 hours of the end of each shift" },
      { token: "[REPORT CONTENTS]", label: "What the shift report must contain", example: "units sampled, units sold, facings and shelf position, out-of-stocks, competitor activity, store contact feedback and two shelf photos" },
      { token: "[INCIDENT CONTACT NAME]", label: "Who incidents are reported to", example: "Sam Ortiz, Field Manager" },
      { token: "[INCIDENT CONTACT DETAILS]", label: "Incident contact phone and email", example: "512-555-0142 / field@example.com" },
      { token: "[INCIDENT REPORT WINDOW]", label: "How quickly an incident must be reported", example: "immediately by phone, and in writing the same day" },
      { token: "[INSURANCE ARRANGEMENT]", label: "Who carries what insurance", example: "the Company carries general and product liability covering demo activity; the Demonstrator carries their own auto insurance" },
      { token: "[CANCELLATION NOTICE]", label: "Notice required to cancel a booked shift", example: "48 hours" },
      { token: "[CANCELLATION FEE]", label: "What is paid for a shift cancelled late by the company", example: "half the shift rate" },
      { token: "[TERM]", label: "Length of the engagement", example: "12 months, renewing month to month" },
      { token: "[TERMINATION NOTICE]", label: "Notice to end the engagement", example: "14 days" },
      { token: "[CONFIDENTIALITY YEARS]", label: "Years confidentiality survives", example: "3" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Texas" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Travis County, Texas" },
    ],

    body: `This Demo Contractor Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE]
(the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business at
  [COMPANY ADDRESS], which markets [PRODUCT CATEGORY] products under the brand [BRAND NAME]
  (the "Company"); and

  [DEMONSTRATOR NAME], an individual residing at [DEMONSTRATOR ADDRESS], email
  [DEMONSTRATOR EMAIL] (the "Demonstrator").

1. THE SERVICES
The Demonstrator will conduct in-store product demonstrations and sampling for the Company's
products in [TERRITORY], at the following locations: [LOCATIONS] (each a "Location", and the work
the "Services").

For each shift, the Services consist of: collecting and transporting product and supplies;
checking in with the store as that store requires; setting up and staffing the demo table;
portioning and serving samples; answering questions using the materials described in Section 6;
recording and reporting the information described in Section 8; breaking down the table; and
returning unused product and equipment.

2. SCHEDULE
Shifts are offered and accepted as follows: [SCHEDULE PROCESS]. A standard shift is
[SHIFT LENGTH]. The Demonstrator is not obliged to accept any shift offered, and the Company is
not obliged to offer any minimum number of shifts.

Once the Demonstrator accepts a shift, the Demonstrator shall work it at the Location and on the
date and time accepted, or give notice under Section 12.

3. RATE, EXPENSES AND PAYMENT
Rate: [RATE].

Expenses: [EXPENSE POLICY]. Any expense above [EXPENSE APPROVAL LIMIT] requires the Company's
written approval before it is incurred. Expenses are reimbursed against receipts.

The Demonstrator shall invoice [INVOICE SCHEDULE], stating the shifts worked, the Locations, the
hours and any approved expenses. The Company shall pay a correct invoice on [PAYMENT TERMS]. The
Demonstrator is responsible for any taxes and contributions arising on amounts paid under this
Agreement.

4. PRODUCT, EQUIPMENT AND SUPPLIES
The Company supplies: [SUPPLIES PROVIDED]. The Demonstrator supplies: [DEMONSTRATOR SUPPLIES].

Product and equipment supplied by the Company remain the Company's property. The Demonstrator
shall use them only for the Services, shall keep them clean and secure, and shall return
everything not consumed at the end of the engagement or on request.

For each shift the Demonstrator shall record, and include in the shift report, the product drawn
(including the lot or date code shown on the case), the quantity served, the quantity discarded
and the quantity returned. The Demonstrator shall not sell the Company's product, give it away
other than as a sample during a shift, or take it home.

5. PRODUCT HANDLING
The Demonstrator agrees to the following practices as obligations under this Agreement. They are
what the Company requires of anyone handling its product; the Demonstrator remains responsible
under Section 7 for determining and meeting whatever else applies at each Location.

The Demonstrator shall:
(a) transport chilled product in a closed cooler with sufficient ice or packs, and hold chilled
    product at or below [COLD HOLDING TEMPERATURE] at all times, including in transit;
(b) check and log the temperature of chilled product [TEMPERATURE CHECK INTERVAL] using the
    thermometer supplied, and stop serving and notify the Company if it is out of range;
(c) wash hands before beginning service, after every break, after handling waste, money or a
    phone, and whenever gloves are changed, using the handwashing facilities the Location provides
    or, where none are available, the hand sanitiser and wipes supplied for that purpose;
(d) wear single-use gloves when portioning or handling product that will be consumed, change them
    whenever they are soiled or after any interruption, and not touch ready-to-eat product with
    bare hands;
(e) tie back or cover hair, keep the table, utensils and surfaces clean and sanitised, and use
    single-use cups, utensils and napkins only;
(f) discard any sample that has been poured, portioned or left uncovered for more than
    [SAMPLE DISCARD WINDOW];
(g) discard any product that a consumer has touched, returned, set down or declined, and any
    opened container at the end of a shift;
(h) not consume, re-serve, repackage or take home any product that has been returned, opened at
    the table, or set out for sampling;
(i) not work a shift while experiencing symptoms of a gastrointestinal or feverish illness, or
    with an uncovered cut or wound on the hands or arms, and notify the Company as early as
    possible if a booked shift is affected; and
(j) keep the ingredient and allergen sheet for every product being sampled visible and legible at
    the table at all times, and read from it rather than answering an allergen question from
    memory.

Responsibility for determining what food handling certification applies to the Services at each
Location, for obtaining it and for the cost of it is allocated as follows:
[CERTIFICATION RESPONSIBILITY]. No shift may be worked before that is in place.

6. WHAT THE DEMONSTRATOR MAY SAY
The Company will provide the Demonstrator with [TALKING POINTS DOCUMENT] and
[PROHIBITED CLAIMS DOCUMENT], and may update either at any time.

The Demonstrator shall describe the product only as those materials describe it. The Demonstrator
shall not state or imply that the product treats, prevents, cures or reduces the risk of any
disease or condition, shall not describe results the materials do not describe, shall not
improvise a benefit, and shall not answer an ingredient or allergen question from memory. Where
the Demonstrator does not know an answer, the Demonstrator shall say so and refer the question to
the Company.

7. STORE RULES AND ACCESS
The Demonstrator shall comply with each Location's rules for demonstrations, including check-in
and check-out, badging and identification, dress, table placement, hours, break policy, parking
and loading, and any requirement the store applies to people working on its floor. The
Demonstrator shall follow the reasonable instructions of store management on site and shall not
argue with store staff; where an instruction conflicts with this Agreement, the Demonstrator shall
follow the store and notify the Company.

The Demonstrator is responsible for determining and holding what is required of the Demonstrator
personally in order to perform the Services at each Location, as allocated in Section 5.

8. REPORTING
The Demonstrator shall submit a shift report [REPORTING DEADLINE], containing: [REPORT CONTENTS],
together with the product record required by Section 4. The report is a deliverable under this
Agreement, and the Company may withhold payment for a shift until the report for it is submitted.

9. BRAND ASSETS
The Company grants the Demonstrator a limited, non-exclusive, revocable, non-transferable licence
to use the Company's names, logos, images and materials solely to perform the Services during the
term. The Demonstrator shall use them only in the form supplied, shall not alter them, shall not
register or use them in any handle, domain, page or account, and shall not represent the
Demonstrator as an employee, officer or spokesperson of the Company. The Demonstrator shall not
post about the Company, its products, its customers or the Services on any public channel without
the Company's prior written approval. The licence ends when this Agreement ends, and the
Demonstrator shall stop using the assets and delete or return them.

10. CONFIDENTIALITY
The Demonstrator will learn non-public information, including unreleased products and packaging,
formulations and ingredient information, pricing and promotional plans, sales and velocity data,
store-level performance, retailer and distributor relationships, and the Company's demo materials
and reporting.

The Demonstrator shall keep all of it confidential, shall use it only to perform the Services, and
shall not disclose it to anyone, including any other brand, broker, agency or retailer, without
the Company's prior written consent. This obligation continues for [CONFIDENTIALITY YEARS] years
after this Agreement ends and continues indefinitely for anything that is a trade secret. It does
not apply to information that becomes public through no act of the Demonstrator.

11. PHOTOGRAPHS, CONTENT AND LIKENESS
Any photograph, video, audio, written report, note or other material the Demonstrator creates in
connection with the Services (the "Content") is created for the Company. The Demonstrator hereby
assigns to the Company all right, title and interest in the Content, including all copyright,
worldwide and for the full term of protection, and waives any moral rights in it to the extent
permitted. The Demonstrator shall deliver the Content with the shift report and shall not use,
publish or license it for any other purpose.

The Demonstrator consents to the Company photographing and filming the Demonstrator at demos and
grants the Company the right to use the Demonstrator's image, likeness, voice and first name in
the Company's marketing, including paid advertising, without further payment.

The Demonstrator shall not photograph or film any consumer, store employee or store interior
without that person's permission and the store's permission, and shall not deliver to the Company
any content of an identifiable individual without telling the Company that no release was taken.

12. CANCELLATION AND NO-SHOWS
Either party may cancel a booked shift on [CANCELLATION NOTICE] notice. If the Company cancels a
booked shift with less notice than that, the Company shall pay [CANCELLATION FEE], unless the
store cancelled the demo or the cancellation was caused by circumstances beyond the Company's
reasonable control. If the Demonstrator does not
attend a booked shift without notice, no fee is payable for it and the Company may decline to
offer further shifts.

13. INCIDENTS
The Demonstrator shall report to [INCIDENT CONTACT NAME] at [INCIDENT CONTACT DETAILS], within
[INCIDENT REPORT WINDOW], any of the following: a consumer reporting illness, an allergic reaction
or any adverse effect after sampling; an injury to anyone at or near the demo; product found
damaged, leaking, mouldy, off-specification, past code or otherwise not fit to serve; a
temperature excursion; a complaint from a store or a consumer; a request or instruction from any
official at the Location; and loss or theft of product or equipment.

The report shall state what happened, when, where, which product and lot code was involved, who
was present and what the Demonstrator did. The Demonstrator shall not discuss any incident with
media, on social channels or with any other brand, shall not admit responsibility on the Company's
behalf, and shall preserve the product involved and any packaging for the Company.

14. INSURANCE
Insurance is arranged as follows: [INSURANCE ARRANGEMENT]. Each party shall provide evidence of
the coverage it carries on the other's reasonable request.

15. RELATIONSHIP OF THE PARTIES
The parties intend that the Demonstrator perform the Services as an independent contractor and not
as an employee of the Company. Consistent with that intention: the Demonstrator is free to accept
or decline any shift; the Demonstrator may perform services for others, subject to Sections 10 and
16; the Demonstrator is not entitled to employee benefits from the Company; and neither party may
bind the other or hold itself out as able to do so.

This Section states what the parties intend. It does not determine how the relationship is
characterised for any other purpose, and each party should take its own advice on that question.

16. NON-SOLICITATION AND CONFLICTS
During the term, the Demonstrator shall not demonstrate, sample or promote a product that competes
with the Company's products at any Location on a day the Demonstrator is working for the Company,
and shall tell the Company before accepting work for a competing brand at a Location the Company
uses. The Demonstrator shall not solicit the Company's retail or distributor contacts on behalf of
another brand using information obtained through the Services.

17. TERM AND TERMINATION
This Agreement runs for [TERM] from the Effective Date. Either party may terminate it on
[TERMINATION NOTICE] written notice. The Company may terminate it immediately if the Demonstrator
breaches Section 5, 6, 10, 11 or 13, or is excluded from a Location by the store.

On termination the Demonstrator shall return all product, equipment, materials and confidential
information, deliver any outstanding reports and Content, and stop using the Company's brand
assets. The Company shall pay for shifts properly worked and approved expenses properly incurred
before termination. Sections 4, 10, 11, 13, 15, 16, 18 and 19 survive.

18. GOVERNING LAW AND VENUE
This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard to
its conflict of laws principles. The parties consent to venue in [VENUE].

19. GENERAL
This Agreement is the entire agreement between the parties regarding the Services and supersedes
all prior discussions. It may be amended only in a writing signed by both parties. The
Demonstrator may not subcontract or assign the Services or send a substitute without the Company's
prior written consent. If any provision is held unenforceable, the remainder continues in effect.
This Agreement may be signed in counterparts, including by electronic signature, each of which is
an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            DEMONSTRATOR

                                          [DEMONSTRATOR NAME]


By: _______________________________       Signature: ________________________

Name: _____________________________       Print name: _______________________

Title: ____________________________       Date: _____________________________

Date: _____________________________       Phone / email: ____________________
`,
  },

  {
    id: "product-sampling-release",
    docId: "product-sampling-release",
    title: "PRODUCT SAMPLING PARTICIPATION RELEASE",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "You are sampling to identifiable participants in a structured setting — a tasting panel, a filmed activation, a private event, a consumer research session — and you want a signed record of what they were shown and what they agreed to. It is a clipboard form, not a contract, so keep it short enough that somebody actually reads it.",
      whyItMatters:
        "Founders reach for a release expecting it to solve the allergy problem, and it does not. What it genuinely does is create a record: that the ingredient and allergen information was there to be read, that the person chose to taste, and that you may use the footage. The protection that matters is upstream — an accurate ingredient sheet visible before anyone tastes anything, and staff who know when to stop serving. The second thing it does is get you the content: a filmed reaction under a form that covers organic posting only will not run as an ad, and that is usually the footage you wanted.",
      negotiateCarefully: [
        {
          point: "The ingredient and allergen sheet has to exist first",
          detail:
            "The form refers to a sheet. If there is no sheet at the table, legible and current for what is actually in the cup that day, the form records nothing. Print it, date it, and put it where a person standing up can read it.",
        },
        {
          point: "Match the media grant to what you will actually do",
          detail:
            "Decide before the event whether the footage may run in paid advertising, on packaging, or only on your own channels, and write that into the media section. Widening it afterwards means finding the person again.",
        },
        {
          point: "Keep it short enough to be read",
          detail:
            "A stranger signing at a table will read about a hundred words. Every clause you add past that point makes the whole form less likely to be read, which is the opposite of what a record is for. Put the detail in the ingredient sheet, not in the release.",
        },
        {
          point: "Minors need a parent or guardian",
          detail:
            "Set the minimum age you will sample to, and require a parent or guardian signature below it. Decide this before the event, because it is a staffing question at the table as much as a paperwork one.",
        },
        {
          point: "Feedback and testimonials are a separate yes",
          detail:
            "A participant's enthusiastic comment becomes a testimonial the moment you publish it, with everything that implies for an ingestible product. Make it a separate opt-in box rather than burying it in the main grant.",
        },
      ],
      redFlags: [
        "The form is being used instead of displaying allergen information, rather than alongside it.",
        "A media grant narrower than the use you already have planned.",
        "No minimum age set, and no guardian line, at an event open to families.",
        "No named contact for someone who feels unwell afterwards.",
        "One form used unchanged for public street sampling, filmed sessions and research panels.",
      ],
      timeToComplete: "5 minutes to fill · signed at the table, before the sample is handed over",
    },

    placeholders: [
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[EVENT NAME]", label: "Event or activation name", example: "Acme Summer Tasting" },
      { token: "[EVENT DATE]", label: "Date", example: "June 6, 2026" },
      { token: "[EVENT LOCATION]", label: "Location", example: "Auditorium Shores, Austin, TX" },
      { token: "[PRODUCT DESCRIPTION]", label: "What is being sampled", example: "Acme Citrus Sparkling Tea, 2 fl oz chilled sample" },
      { token: "[INGREDIENT SHEET LOCATION]", label: "Where the ingredient and allergen sheet is displayed", example: "the printed sheet on the front of the sampling table" },
      { token: "[MEDIA USE SCOPE]", label: "How photos and video may be used", example: "the brand's own website and social channels, paid social and digital advertising, trade materials and press, worldwide, with no time limit" },
      { token: "[MINIMUM AGE]", label: "Minimum age to take part without a guardian", example: "18" },
      { token: "[CONTACT NAME]", label: "Who to contact about a reaction or a question", example: "Sam Ortiz, Acme Beverage Co." },
      { token: "[CONTACT DETAILS]", label: "Contact phone and email", example: "512-555-0142 / hello@example.com" },
    ],

    body: `[BRAND NAME] PRODUCT SAMPLING — PARTICIPATION RELEASE

[COMPANY NAME], [COMPANY ADDRESS]
Event: [EVENT NAME] · [EVENT DATE] · [EVENT LOCATION]

Please read this before you taste anything. Ask us if anything is unclear.


1. WHAT YOU ARE SAMPLING
[PRODUCT DESCRIPTION].

2. INGREDIENTS AND ALLERGENS — PLEASE READ BEFORE YOU CONSUME
The full ingredient and allergen information for this product is available at
[INGREDIENT SHEET LOCATION]. Please read it before you consume the sample, and ask us if you have
a question about any ingredient.

Only you know what you can and cannot consume. If you have a food allergy, an intolerance, a
sensitivity, a medical condition, a dietary restriction, or you are pregnant or nursing, please
check the ingredient information first and do not take the sample if you are unsure. Samples are
prepared and served at an event rather than in a controlled kitchen, so contact between different
products and ingredients is possible.

If you feel unwell after sampling, please tell a member of our team, and contact
[CONTACT NAME] at [CONTACT DETAILS] so we have a record of it.

3. TAKING PART IS YOUR CHOICE
You are taking part voluntarily. Nobody is requiring you to taste anything, you may ask questions
first, you may stop at any point, and you may decline the sample for any reason or no reason. You
are confirming that you have read Section 2 and that you have decided to take part on that basis.

4. PHOTOS AND VIDEO
We may take photographs, video and audio at this activation, and you may appear in them. By
signing, you agree that we and anyone acting for us may use your image, likeness, voice and first
name as they appear in that material, in [MEDIA USE SCOPE], without further payment or approval.
We will not present you as endorsing or recommending the product unless you initial Section 5.

If you would rather not appear, tell us and we will keep you out of shot — just do not sign this
Section.

5. FEEDBACK AND TESTIMONIALS — OPTIONAL
Initial here only if you agree that we may quote what you tell us about the product, alongside
your first name and the first initial of your last name, in our marketing:

  Initials: __________

Leaving this blank does not stop you taking part, and does not stop us using your feedback
anonymously to improve the product.

6. IF YOU ARE UNDER [MINIMUM AGE]
You must be at least [MINIMUM AGE] to sample on your own signature. If you are younger than that,
a parent or legal guardian must read this form and sign the second signature block below before
you take a sample.

7. QUESTIONS
[CONTACT NAME] · [CONTACT DETAILS]


PARTICIPANT


Signature: ________________________________   Date: ________________________

Print name: _______________________________   Age, if under [MINIMUM AGE]: ______

Email or phone (optional): ________________________________________________


PARENT OR LEGAL GUARDIAN — required if the participant is under [MINIMUM AGE]

I am the parent or legal guardian of the participant named above. I have read this form, including
the ingredient and allergen information referred to in Section 2, and I agree to it on their
behalf and on my own.


Signature: ________________________________   Date: ________________________

Print name: _______________________________   Relationship: ________________

Phone or email: ___________________________________________________________
`,
  },
];

export default EVENTS_TEMPLATES;
